// Pages/admin/EditSubscriptionPage.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiSave,
  FiArrowLeft,
  FiPlus,
  FiTrash2,
  FiPackage,
  FiDollarSign,
  FiEdit3,
  FiAlertCircle,
} from "react-icons/fi";
import { useSubscriptionPlanStore } from "../../store/subscriptionPlanStore";
import { useServiceStore } from "../../store/serviceStore";

export default function EditSubscriptionPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    currentSubscription,
    loading,
    error,
    fetchSubscriptionById,
    updateSubscription,
    clearError,
    clearCurrentSubscription,
  } = useSubscriptionPlanStore();

  const { services: availableServices, loadServices } = useServiceStore();

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    isActive: true,
    services: [],
  });

  const [newService, setNewService] = useState({ id: "", quantity: 1 });

  useEffect(() => {
    if (id) {
      fetchSubscriptionById(id);
    }
    return () => clearCurrentSubscription();
  }, [id, fetchSubscriptionById, clearCurrentSubscription]);

  useEffect(() => {
    if (currentSubscription) {
      setFormData({
        name: currentSubscription.name,
        description: currentSubscription.description,
        price: parseFloat(currentSubscription.price).toString(),
        isActive: currentSubscription.isActive,
        services:
          currentSubscription.includedServices?.map((service) => ({
            id: service.service?.id?.toString() || service.id.toString(),
            quantity: service.quantity,
          })) || [],
      });
    }
  }, [currentSubscription]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      const subscriptionData = {
        ...formData,
        price: parseFloat(formData.price),
        services: formData.services.map((service) => ({
          id: parseInt(service.id),
          quantity: parseInt(service.quantity),
        })),
      };

      await updateSubscription(id, subscriptionData);
      navigate("/admin/subscriptions");
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
    }
  };

  const addService = () => {
    if (newService.id && newService.quantity > 0) {
      const serviceExists = formData.services.find(
        (s) => s.id === newService.id,
      );
      if (!serviceExists) {
        setFormData((prev) => ({
          ...prev,
          services: [...prev.services, { ...newService }],
        }));
        setNewService({ id: "", quantity: 1 });
      }
    }
  };

  const removeService = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s.id !== serviceId),
    }));
  };

  const getServiceName = (serviceId) => {
    const service = availableServices.find((s) => s.id === parseInt(serviceId));
    return service ? service.nom : `Service #${serviceId}`;
  };

  if (loading && !currentSubscription) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600 mx-auto"></div>
          <p className="mt-4 font-nunito text-[14px] text-text font-medium">
            Chargement de l'abonnement...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* En-tête */}
        <button
          onClick={() => navigate("/admin/subscriptions")}
          className="bg-white mb-2 hover:bg-gray-50 text-gray-700 p-3  shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-md"
        >
          <FiArrowLeft size={20} />
        </button>
        <div className="mb-4">
          <h1 className="font-poppins font-semibold text-[18px] text-title">
            Modifier l'abonnement
          </h1>
          <p className="font-nunito text-[14px] text-text mt-1">
            Mettez à jour les informations de votre formule d'abonnement
          </p>
        </div>

        {/* Carte principale */}
        <div className="bg-white  shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-secondary-600 p-2 md:p-6">
            <div className="flex items-center space-x-3">
              <FiPackage className="text-white" size={18} />
              <div>
                <h2 className="font-poppins font-semibold text-[18px] text-white">
                  {currentSubscription?.name || "Abonnement"}
                </h2>
                <p className="font-nunito text-[14px] text-secondary-100 text-xs">
                  Code: {currentSubscription?.code}
                </p>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-2 md:p-6 space-y-3 md:space-y-6"
          >
            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-200  p-4">
                <div className="flex items-center space-x-3 text-red-800">
                  <FiAlertCircle size={20} />
                  <span className="font-poppins font-medium text-[15px] text-subtitle">
                    {error}
                  </span>
                </div>
              </div>
            )}

            {/* Informations de base */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-6">
              <div>
                <label className="font-poppins font-medium text-[15px] text-subtitle block mb-3">
                  Nom de l'abonnement *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-4 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200"
                  placeholder="Ex: Formule Relaxe"
                />
              </div>

              <div>
                <label className="font-poppins font-medium text-[15px] text-subtitle block mb-3">
                  Prix (FCFA) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiDollarSign className="text-gray-400" size={20} />
                  </div>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200"
                    placeholder="30000"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="font-poppins font-medium text-[15px] text-subtitle block mb-3">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200"
                placeholder="Ex: 2 massages + 1 gommage"
              />
            </div>

            {/* Services inclus */}
            <div className="bg-gray-50 p-2 md:p-6 ">
              <label className="font-poppins font-medium text-[15px] text-subtitle block mb-4">
                <FiPackage className="inline mr-2" size={16} />
                Services inclus
              </label>

              {/* Ajout d'un nouveau service */}
              <div className="flex flex-col mb-3 items-center gap-2 mt-4 lg:mt-0 md:flex-row md:gap-2">
                {/* Sélecteur de service */}
                <div className="flex-1 w-full">
                  <select
                    value={newService.id}
                    onChange={(e) =>
                      setNewService((prev) => ({ ...prev, id: e.target.value }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200"
                  >
                    <option value="">Sélectionner un service</option>
                    {availableServices.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.nom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantité + Ajouter */}
                <div className="flex flex-row items-center justify-center gap-2 w-full md:w-auto">
                  <div className="w-24">
                    <input
                      type="number"
                      min="1"
                      value={newService.quantity}
                      onChange={(e) =>
                        setNewService((prev) => ({
                          ...prev,
                          quantity: parseInt(e.target.value) || 1,
                        }))
                      }
                      className="w-full px-4 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200"
                      placeholder="Qté"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addService}
                    disabled={!newService.id}
                    className="flex-1 bg-secondary-500 hover:bg-secondary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 font-nunito font-semibold text-[14px] inline-flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg md:flex-none"
                  >
                    <FiPlus size={18} />
                    <span>Ajouter</span>
                  </button>
                </div>
              </div>

              {/* Liste des services ajoutés */}
              {formData.services.length > 0 ? (
                <div className="space-y-1 md:space-y-3">
                  {formData.services.map((service, index) => (
                    <div
                      key={service.id}
                      className="flex items-center justify-between p-4 bg-white border border-gray-200  hover:shadow-sm transition-all duration-200"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-secondary-50 ">
                          <FiPackage className="text-secondary-500" size={16} />
                        </div>
                        <div>
                          <span className="font-poppins font-medium text-[15px] text-subtitle">
                            {getServiceName(service.id)}
                          </span>
                          <span className="font-nunito text-[14px] text-text ml-2">
                            ×{service.quantity}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeService(service.id)}
                        className="text-red-500 hover:text-red-700 p-2  hover:bg-red-50 transition-all duration-200"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-gray-300  bg-white">
                  <FiPackage className="mx-auto text-gray-400 mb-3" size={32} />
                  <p className="font-poppins font-medium text-[15px] text-subtitle">
                    Aucun service ajouté
                  </p>
                  <p className="font-nunito text-[14px] text-text mt-1">
                    Ajoutez des services à votre abonnement
                  </p>
                </div>
              )}
            </div>

            {/* Statut */}
            <div className="flex flex-col  mb-3   md:flex-row gap-2 md:space-x-4 p-4 bg-gray-5">
              <div className="inline-flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 text-secondary-500 border-gray-300 focus:ring-secondary-500"
                />
                <label
                  htmlFor="isActive"
                  className="font-poppins font-medium text-[15px] text-subtitle"
                >
                  Abonnement actif
                </label>
              </div>
              <div
                className={`px-3 py-1 font-nunito text-[14px] font-medium ${
                  formData.isActive
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : "bg-gray-100 text-gray-800 border border-gray-200"
                }`}
              >
                {formData.isActive
                  ? "✅ Actuellement actif"
                  : "❌ Actuellement inactif"}
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col gap-3 mt-4 mb-3 items-stretch md:flex-row md:gap-2 md:items-center lg:mt-0">
              <button
                type="button"
                onClick={() => navigate("/admin/subscriptions")}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-4 font-nunito font-semibold text-[14px] transition-all duration-200 hover:shadow-lg"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-secondary-600 to-secondary-100 hover:from-secondary-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-6 py-4 font-nunito font-semibold text-[14px] inline-flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg"
              >
                <FiSave size={20} />
                <span>{loading ? "Mise à jour..." : "Mettre à jour"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
